import React, { useEffect, useMemo, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import { Button } from 'antd';
import moment from 'moment';

import { connect } from '../StateProvider';
import Loading from '../common/Loading';
import getLink from '../common/router/getLink';
import routes from '../../routes';
import { VIEW_MODES, SETTINGS_KEYS } from '../../configs/viewSettings';
import { decode } from '../../utils/queryParams';

import UrlParamSync from '../UrlParamSync';
import GanttHeader from './Header';
import GanttBody from './Body';
import useGanttRecords, { STATUS } from './hooks/useGanttRecords.hook';
import useViewSettings from './hooks/useViewSettings.hook';
import useTaskEdit from './hooks/useTaskEdit.hook';
import useLinkEdit from './hooks/useLinkEdit.hook';
import { isSelfLinkField } from './helpers/links';
import { SCALES } from './helpers/scales';
import availableFieldTypes from './helpers/availableFieldTypes';
import styles from './gantt.less';

const GanttDataProvider = (props) => {
  const {
    startId,
    needsDefaultStart,
    endId,
    titleId,
    employeeId,
    progressId,
    scale,
    catalogId,
    sceneId,
    viewId,
    filters,
    fields,
    records,
    recordsCount,
    shouldReload,
    gridWidth,
    displayMode,
    savedColumnWidths,
    colorField,
    linkField,
    linkDirection,
    startField,
    endField,
    history,
    location,
    t,
    selectedRowsIds,
    onRowSelectionChange,
  } = props;

  const bodyRef = useRef(null);
  const colorId = colorField && colorField.get('id');

  const columnWidths = useMemo(
    () => (savedColumnWidths && savedColumnWidths.toJS ? savedColumnWidths.toJS() : savedColumnWidths) || null,
    [savedColumnWidths],
  );

  const { status, build } = useGanttRecords({
    catalogId,
    sceneId,
    viewId,
    filters,
    records,
    recordsCount,
    shouldReload,
    startId,
    endId,
    titleId,
    employeeId,
    progressId,
    colorId,
    linkFieldId: linkField && linkField.get('id'),
  });

  const scaleConfig = SCALES[scale] || SCALES.day;
  const editTask = useTaskEdit({
    records,
    catalogId,
    sceneId,
    startId,
    endId,
    progressId,
    stepUnit: scaleConfig.stepUnit,
    cell: scaleConfig.scales[scaleConfig.scales.length - 1],
  });

  const editLink = useLinkEdit({ records, catalogId, sceneId, linkField, linkDirection });

  const { saveScale, saveGridWidth, saveColumnWidths, saveDisplayMode, changeFieldOption, changeLinkDirection } =
    useViewSettings({
      catalogId,
      viewId,
      fields,
    });

  useEffect(() => {
    if (!needsDefaultStart) return;
    if (decode(location.search, SETTINGS_KEYS.GANTT_START)[SETTINGS_KEYS.GANTT_START]) return;
    const field = fields && fields.find((f) => availableFieldTypes.start.includes(f.get('type')));
    if (field) changeFieldOption(SETTINGS_KEYS.GANTT_START)(field.get('id'));
  }, [needsDefaultStart, fields, location.search, changeFieldOption]);

  const changeScale = (value, { replace } = {}) => {
    if (!SCALES[value] || value === scale) return;

    const link = getLink(location, routes.ganttSettings, { scale: value });
    if (replace) history.replace(link);
    else history.push(link);

    saveScale(value);
  };

  const zoomScale = (value) => changeScale(value, { replace: true });

  const scrollToToday = () => bodyRef.current && bodyRef.current.scrollTo(moment());

  const renderContent = () => {
    if (!startId) return <div className={styles.empty}>{t('gantt.noStartField')}</div>;

    if (status === STATUS.CONFIRM) {
      return (
        <Button className={styles.renderButton} onClick={build}>
          {t('gantt.build')}
        </Button>
      );
    }

    if (status !== STATUS.READY) {
      return (
        <div className={styles.empty}>
          <Loading className={styles.loading} />
        </div>
      );
    }

    return (
      <GanttBody
        ref={bodyRef}
        records={records}
        startId={startId}
        endId={endId}
        titleId={titleId}
        employeeId={employeeId}
        progressId={progressId}
        scale={scale}
        onScaleChange={zoomScale}
        onEditTask={editTask}
        sceneId={sceneId}
        catalogId={catalogId}
        fields={fields}
        gridWidth={gridWidth}
        displayMode={displayMode}
        columnWidths={columnWidths}
        linkField={linkField}
        linkDirection={linkDirection}
        onEditLink={editLink}
        colorField={colorField}
        startField={startField}
        endField={endField}
        onGridWidthChange={saveGridWidth}
        onDisplayModeChange={saveDisplayMode}
        onColumnWidthsChange={saveColumnWidths}
        selectedRowsIds={selectedRowsIds}
        onRowSelectionChange={onRowSelectionChange}
      />
    );
  };

  return (
    <div className={styles.container}>
      <GanttHeader scale={scale} onScaleChange={changeScale} onToday={scrollToToday} />
      {renderContent()}
      {[
        [SETTINGS_KEYS.GANTT_START, startId],
        [SETTINGS_KEYS.GANTT_END, endId],
        [SETTINGS_KEYS.GANTT_TITLE, titleId],
        [SETTINGS_KEYS.GANTT_EMPLOYEE, employeeId],
        [SETTINGS_KEYS.GANTT_PROGRESS, progressId],
        [SETTINGS_KEYS.GANTT_LINK_FIELD, linkField && linkField.get('id')],
      ].map(([option, value]) => (
        <UrlParamSync key={option} name={option} value={value} onChange={changeFieldOption(option)} />
      ))}
      <UrlParamSync name={SETTINGS_KEYS.GANTT_LINK_DIRECTION} value={linkDirection} onChange={changeLinkDirection} />
    </div>
  );
};

export default connect(
  withRouter(withTranslation()(GanttDataProvider)),
  {
    viewSettings: ['viewSettings', 'catalogs'],
  },
  (props, { viewSettings }) => {
    const { catalogId, viewId, scene, catalog } = props;

    const settings = viewSettings.getIn([catalogId, 'views', viewId, VIEW_MODES.GANTT]);
    const fields = catalog && catalog.get('fields');

    const startId = settings && settings.getIn([SETTINGS_KEYS.GANTT_START, SETTINGS_KEYS.FIELD_ID]);
    const endId = settings && settings.getIn([SETTINGS_KEYS.GANTT_END, SETTINGS_KEYS.FIELD_ID]);
    const colorId = settings && settings.getIn([SETTINGS_KEYS.COLOR, SETTINGS_KEYS.FIELD_ID]);
    const linkFieldId = settings && settings.getIn([SETTINGS_KEYS.GANTT_LINK_FIELD, SETTINGS_KEYS.FIELD_ID]);
    const linkField =
      fields && fields.find((field) => field.get('id') === linkFieldId && isSelfLinkField(field, catalogId));
    const savedWidths = settings && settings.get(SETTINGS_KEYS.GANTT_COLUMN_WIDTHS);

    return {
      startId,
      linkField,
      linkDirection:
        settings && settings.get(SETTINGS_KEYS.GANTT_LINK_DIRECTION) === 'successors' ? 'successors' : 'predecessors',
      needsDefaultStart: viewSettings.hasIn([catalogId, 'views', viewId]) && !settings?.has(SETTINGS_KEYS.GANTT_START),
      endId,
      titleId: settings && settings.getIn([SETTINGS_KEYS.GANTT_TITLE, SETTINGS_KEYS.FIELD_ID]),
      employeeId: settings && settings.getIn([SETTINGS_KEYS.GANTT_EMPLOYEE, SETTINGS_KEYS.FIELD_ID]),
      progressId: settings && settings.getIn([SETTINGS_KEYS.GANTT_PROGRESS, SETTINGS_KEYS.FIELD_ID]),
      gridWidth: settings && settings.get(SETTINGS_KEYS.GANTT_GRID_WIDTH),
      displayMode: settings && settings.get(SETTINGS_KEYS.GANTT_DISPLAY_MODE) || 'chart',
      savedColumnWidths: savedWidths || null,
      colorField: (colorId && fields && fields.find((f) => f.get('id') === colorId)) || null,
      startField: (startId && fields && fields.find((f) => f.get('id') === startId)) || null,
      endField: (endId && fields && fields.find((f) => f.get('id') === endId)) || null,
      records: scene && scene.get('ganttRecords'),
      recordsCount: scene && scene.get('ganttRecordsCount'),
      shouldReload: scene && scene.get('shouldReload'),
      filters: scene && scene.getIn(['views', viewId, 'filters']),
      fields,
      ...props,
    };
  },
);
